
import { defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// Components
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// Interfaces & Types
import {
    AdditionalServicesI,
    AdditionalServicesLTLPreloadI,
} from '@/core/types/additional_services.types'
import useVuelidate from '@vuelidate/core'
import { decimal, integer, requiredIf } from '@vuelidate/validators'
import { FTLBidAdditionalServiceT } from '@/core/types/ftl_bids.types'

export default defineComponent({
    components: {
        checkbox: Checkbox,
        'the-button': TheButton,
        'the-input': TheInput,
    },
    props: {
        setted: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        isLtlEdit: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<AdditionalServicesLTLPreloadI>,
            default: null,
        },
        initialValueFtl: {
            type: Array as PropType<FTLBidAdditionalServiceT[]>,
            default: () => [],
        },
        isReceiver: {
            type: Boolean,
            default: false,
        },
        isSender: {
            type: Boolean,
            default: false,
        },
        isAdditionalServicesVisible: {
            type: Boolean,
            default: false,
        },
        waypointName: {
            type: String,
            default: null,
        },
    },
    emits: ['submit', 'reset', 'submit-ltl'],
    setup(props, { emit }) {
        const state: AdditionalServicesI = reactive({
            hasCar: false,
            hasSoftPackage: false,
            hasManipulator: false,
            hasCrane: false,
            hasHydraulicTrolley: false,
            hasGrid: false,
            hasLoader: false,
            hasPallet: false,
        })

        const state_ltl = reactive({
            carCost: null as number | null,
            manipulatorCost: null as number | null,
            craneCost: null as number | null,
            hydraulicTrolleyCost: null as number | null,
            amountOfLoaders: null as number | null,
            hoursOfLoadersWork: null as number | null,
            palletCost: null as number | null,
        })

        const state_ftl = reactive({
            loader_value: null as number | null,
        })

        let UpdateLTLAdditionalServicesValidation = {
            carCost: {
                required: requiredIf(() => {
                    return props.isLtlEdit && state.hasCar
                }),
                decimal,
            },
            manipulatorCost: {
                required: requiredIf(() => {
                    return props.isLtlEdit && state.hasManipulator
                }),
                decimal,
            },
            craneCost: {
                required: requiredIf(() => {
                    return props.isLtlEdit && state.hasCrane
                }),
                decimal,
            },
            hydraulicTrolleyCost: {
                required: requiredIf(() => {
                    return props.isLtlEdit && state.hasHydraulicTrolley
                }),
                decimal,
            },
            palletCost: {
                required: requiredIf(() => {
                    return props.isLtlEdit && state.hasPallet
                }),
                decimal,
            },
            amountOfLoaders: {
                required: requiredIf(() => {
                    return props.isLtlEdit && state.hasLoader
                }),
                integer,
            },
            hoursOfLoadersWork: {
                required: requiredIf(() => {
                    return props.isLtlEdit && state.hasLoader
                }),
                integer,
            },
        }

        let FTLAdditionalServicesValidation = {
            loader_value: {
                required: requiredIf(() => {
                    return props.initialValueFtl && state.hasLoader
                }),
                integer,
            },
        }

        const state_form$ = useVuelidate(UpdateLTLAdditionalServicesValidation, state_ltl)

        const state_ftl$ = useVuelidate(FTLAdditionalServicesValidation, state_ftl)

        const emitSubmit = () => {
            if (props.loading) return

            if (props.isLtlEdit) {
                state_form$.value.$touch()
                if (state_form$.value.$invalid) return

                emit('submit-ltl', { ...state, ...state_ltl })
            } else {
                state_ftl$.value.$touch()
                if (state_ftl$.value.$invalid) return

                emit('submit', {
                    ...state,
                    hasLoader: state.hasLoader ? { value: state_ftl.loader_value } : false,
                })
            }
        }

        const emitReset = () => {
            state.hasCar = false
            state_ltl.carCost = null

            state.hasManipulator = false
            state_ltl.manipulatorCost = null

            state.hasCrane = false
            state_ltl.craneCost = null

            state.hasHydraulicTrolley = false
            state_ltl.hydraulicTrolleyCost = null

            state.hasPallet = false
            state_ltl.palletCost = null

            state.hasLoader = false
            state_ltl.amountOfLoaders = null
            state_ltl.hoursOfLoadersWork = null

            state.hasGrid = false
            state.hasSoftPackage = false
            state.hasPallet = false

            emit('reset')
        }

        const onHasLoaderChange = () => {
            state_ltl.amountOfLoaders = null
            state_ltl.hoursOfLoadersWork = null
        }

        const setInitialValueFTL = () => {
            if (props.initialValueFtl) {
                props.initialValueFtl.forEach((service) => {
                    switch (service.type_code) {
                        case 'car':
                            state.hasCar = true
                            break
                        case 'soft_package':
                            state.hasSoftPackage = true
                            break
                        case 'manipulator':
                            state.hasManipulator = true
                            break
                        case 'crane':
                            state.hasCrane = true
                            break
                        case 'hydraulic_trolley':
                            state.hasHydraulicTrolley = true
                            break
                        case 'grid':
                            state.hasGrid = true
                            break
                        case 'loader':
                            state.hasLoader = true
                            state_ftl.loader_value = service.value
                            break
                        case 'pallet':
                            state.hasPallet = true
                            break
                    }
                })
            }
        }

        onBeforeMount(() => {
            if (props.initialValue) {
                state.hasCar = props.initialValue.car
                state_ltl.carCost = props.initialValue.car_cost

                state.hasManipulator = props.initialValue.manipulator
                state_ltl.manipulatorCost = props.initialValue.manipulator_cost

                state.hasCrane = props.initialValue.crane
                state_ltl.craneCost = props.initialValue.crane_cost

                state.hasHydraulicTrolley = props.initialValue.hydraulic_trolley
                state_ltl.hydraulicTrolleyCost = props.initialValue.hydraulic_trolley_cost

                state.hasPallet = props.initialValue.pallet
                state_ltl.palletCost = props.initialValue.pallet_cost

                state.hasLoader = props.initialValue.loader
                state_ltl.amountOfLoaders = props.initialValue.amount_of_loaders
                state_ltl.hoursOfLoadersWork = props.initialValue.hours_of_loaders_work

                state.hasGrid = props.initialValue.grid
                state.hasSoftPackage = props.initialValue.soft_package
            }

            setInitialValueFTL()
        })

        return {
            state,
            state_ltl,
            state_ftl,
            state_form$,
            state_ftl$,
            //
            emitSubmit,
            emitReset,
            onHasLoaderChange,
        }
    },
})
